import React, { memo, useMemo, useCallback } from 'react';
import { useToast, useOpenList, useLists } from '../../context';
import { useLang } from '../../i18n';
import { useListNames } from '../../hooks/useListNames';
import { useSaveFailed } from '../../hooks/useSaveFailed';
import { isHandled, sortItems } from '../../logic/items';
import { normalizeName } from '../../logic/names';
import { localDateISO } from '../../logic/dates';
import { USUAL_COUNT } from '../../config/constants';
import { addItems, setItemsArchived } from '../../data/items';
import { AddCatalog } from './AddCatalog';
import { ItemRow } from './ItemRow';

// Task-list done verbs for the panel's "N prepped / cooked …" line.
const TASK_VERBS = { prep: 'prepped', cooking: 'cooked', cleaning: 'cleaned', issues: 'resolved' };

/**
 * One list on the Today board: what's on it (left) and the picker (right). Memoized —
 * it re-renders when its own items, suggestions or flags change, not when another list does.
 *
 *   list, items, catalog      the list, its open items, its suggestions
 *   hideHandled, collapsed    view flags
 *   onToggleCollapse(id) · onSettings(list) · addTo(listId, name, fromListId) · elsewhere(fromId, nq)
 *   actions, moveTargets      for the rows (see ItemRow)
 */
export const ListPanel = memo(function ListPanel({
  list, items, catalog, author, hideHandled, collapsed, onToggleCollapse, onSettings, addTo, elsewhere, actions, moveTargets,
}) {
  const notify = useToast();
  const failed = useSaveFailed();
  const { t } = useLang();
  const { route } = useLists();
  const { name: listName, short } = useListNames();
  const { withList } = useOpenList();
  const sorted = useMemo(() => sortItems(items), [items]);   // urgent first, then oldest-added
  const visible = useMemo(() => (hideHandled ? sorted.filter(i => !isHandled(i)) : sorted), [sorted, hideHandled]);
  const taken = useMemo(() => new Set(items.map(i => normalizeName(i.name))), [items]);
  const done = useMemo(() => items.filter(isHandled).length, [items]);
  const title = listName(list);

  // A typed name or suggestion lands in the list its name lives in (a moved item, or a
  // chef ingredient typed into Groceries → Chef).
  const add = useCallback(name => {
    const clean = String(name || '').trim();
    if (clean) addTo(route(clean, list.id), clean, list.id);
  }, [addTo, route, list.id]);
  const onElsewhere = useCallback(nq => elsewhere(list.id, nq), [elsewhere, list.id]);
  const onAddElsewhere = useCallback((listId, name) => addTo(listId, name, list.id), [addTo, list.id]);
  const destOf = useCallback(name => { const to = route(name, list.id); return to !== list.id ? to : null; }, [route, list.id]);

  // "✨ Add my usual" — re-add the staples history says you usually need, in one write.
  const usual = useMemo(
    () => catalog.filter(s => s.fromHistory && !taken.has(normalizeName(s.name))).slice(0, USUAL_COUNT),
    [catalog, taken],
  );
  const addUsual = () => withList(listFor => {
    const today = localDateISO();
    addItems(usual.map(s => ({ date: today, listFor, category: list.id, name: s.name, addedBy: author }))).saved.catch(failed);
  }, t('list.addingN', { n: usual.length }));

  // "Move to history →" clears the handled items off the board (History keeps them), so
  // a busy day can note the same item again.
  const doneVerb = t(`verb.${list.sourceable ? 'refilled' : (TASK_VERBS[list.id] || 'done')}`);
  const archiveDone = () => {
    const ids = items.filter(isHandled).map(i => i.id);
    if (!ids.length) return;
    setItemsArchived(ids, true, author).catch(failed);
    notify(t('today.movedToHistory', { n: ids.length }), () => setItemsArchived(ids, false).catch(failed));
  };

  return (
    <section className={`do-panel${collapsed ? ' do-panel--collapsed' : ''}`} style={{ '--cc': list.color }} aria-label={title}>
      <div className="do-panel__head">
        <button className="do-panel__toggle" onClick={() => onToggleCollapse(list.id)} aria-expanded={!collapsed}>
          <span className="do-panel__icon">{list.icon}</span>
          <span className="do-panel__title">{title}</span>
          <span className="do-panel__count">{items.length > 0 ? <>{done}/{items.length}</> : '0'}</span>
          <span className="do-panel__chev">{collapsed ? '▾' : '▴'}</span>
        </button>
        <button className="do-panel__gear" onClick={() => onSettings(list)}
          aria-label={t('lists.settingsFor', { name: title })} title={t('lists.settingsFor', { name: title })}>⚙︎</button>
      </div>

      {!collapsed && (
        <div className="do-panel__panes">
          {/* Left — what's on the list (scrolls if long, so the picker stays put) */}
          <div className="do-panel__list">
            {visible.length > 0 && (
              <div className="do-items">
                {visible.map(it => <ItemRow key={it.id} item={it} list={list} actions={actions} moveTargets={moveTargets} />)}
              </div>
            )}
            {items.length > 0 && visible.length === 0 && (
              <p className="do-panel__allclear">{t('today.allClear', { verb: doneVerb })}</p>
            )}
            {done > 0 && (
              <div className="do-panel__archive">
                <span className="do-panel__archive-lbl">{t('today.doneCount', { n: done, verb: doneVerb })}</span>
                <button className="do-panel__move" onClick={archiveDone} title={t('today.moveToHistoryHint')}>{t('today.moveToHistory')}</button>
              </div>
            )}
            {items.length === 0 && (usual.length > 0
              ? <button className="do-panel__rebuild" onClick={addUsual}>{t('today.addUsual', { n: usual.length })}</button>
              : <p className="do-panel__emptyhint">{t('today.nothingYet')}</p>
            )}
          </div>
          {/* Right — the picker: search + "usually needed", stays fixed as the list grows */}
          <div className="do-panel__add">
            <AddCatalog catalog={list.noSuggest ? NO_SUGGESTIONS : catalog} taken={taken} color={list.color}
              onAdd={add} placeholder={t('today.searchAdd', { list: short(list) })}
              elsewhere={onElsewhere} onAddElsewhere={onAddElsewhere} destOf={destOf} />
          </div>
        </div>
      )}
    </section>
  );
});

const NO_SUGGESTIONS = [];   // Issues is free-form — one shared empty array keeps the memo
